import { List, Typography, useTheme } from '@mui/material';
import { useMemo } from 'react';

import { OrderModel, OrderStatusModel } from '@merchant-clients/order';

import { StatusHeader, ViewSection } from '@frontend/components';
import { tOrder } from '@frontend/locales';

import {
  GroupByStatusModel,
  GroupedOrdersModel,
  OrdersGroupByFieldModel,
} from '../hooks';
import { OrderListItem } from './order-list-item.component';

export type VariantType = 'neutral' | 'warning' | 'success' | 'error';

const statusTitleMap: Record<
  Exclude<OrderStatusModel, 'REJECTED' | 'REFUNDED'>,
  () => string
> = {
  PENDING: tOrder.orderList.orderAwaiting,
  CONFIRMED: tOrder.orderList.orderConfirmed,
  READY: tOrder.orderList.orderReady,
  CLOSED: tOrder.orderList.orderClosed,
};

export const statusVariantMap: Record<OrderStatusModel, VariantType> = {
  PENDING: 'neutral',
  CONFIRMED: 'warning',
  READY: 'success',
  CLOSED: 'error',
  REJECTED: 'error',
  REFUNDED: 'error',
};

export interface OrderListProps {
  orders: GroupedOrdersModel;
  variant?: VariantType;
  groupBy: OrdersGroupByFieldModel;
}

export const OrderList = ({ orders, variant, groupBy }: OrderListProps) => {
  const theme = useTheme();
  const groups = useMemo(() => Object.keys(orders), [orders]);

  return (
    <>
      {groups.map((key, index) => {
        return (
          <ViewSection
            fullWidth
            sx={{
              p: 3,
              backgroundColor:
                index % 2 === 0 ? undefined : theme.palette.action.hover,
              height: groups.length === index + 1 ? '100%' : undefined,
            }}
            key={key}
          >
            {groupBy === 'STATUS' && (
              <StatusHeader
                size="large"
                variant={statusVariantMap[key as keyof GroupByStatusModel]}
              >
                {statusTitleMap[key as keyof GroupByStatusModel]()}
              </StatusHeader>
            )}

            {groupBy === 'DATE' && (
              <Typography variant="h6" gutterBottom>
                {key}
              </Typography>
            )}
            <List
              sx={{
                width: '100%',
                display: 'flex',
                flexWrap: 'wrap',
                gap: 2,
                alignItems: 'flex-start',
                minHeight: '170px',
              }}
            >
              {(orders as Record<string, OrderModel[]>)[key]!.map((order) => (
                <OrderListItem key={order.id} order={order} variant={variant} />
              ))}
            </List>
          </ViewSection>
        );
      })}
    </>
  );
};
